import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/tmp/3213c65a/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import ColorBlock from 'components/ColorBlock';
import ColorGrid from 'components/ColorGrid';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const Video = makeShortcode("Video");
const Row = makeShortcode("Row");
const Column = makeShortcode("Column");
const ResourceCard = makeShortcode("ResourceCard");
const MdxIcon = makeShortcode("MdxIcon");
const Caption = makeShortcode("Caption");
const DoDontExample = makeShortcode("DoDontExample");
const ArtDirection = makeShortcode("ArtDirection");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3 {...{
      "id": "maintaining-consistent-and-engaging-digital-interfaces-throughout-ibm-whether-applications-or-experiences-demands-extended-guidance-around-color-usage-the-following-concepts-are-the-foundation-as-we-strive-to-achieve-balance-and-harmony-through-our-user-interface-design"
    }}>{`Maintaining consistent and engaging digital interfaces throughout IBM, whether applications or experiences, demands extended guidance around color usage. The following concepts are the foundation as we strive to achieve balance and harmony through our User Interface design.`}</h3>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Introduction</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Color anatomy</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Implementing color</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Accessibility</AnchorLink>
    </AnchorLinks>
    <Video vimeoId="281513790" mdxType="Video" />
    <h2 {...{
      "id": "resources"
    }}>{`Resources`}</h2>
    <Row className="resource-card-group" mdxType="Row">
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="RGB color palettes (.ase and .clr)" href="https://www.carbondesignsystem.com/files/IBM_Colors_RGB.zip" mdxType="ResourceCard">
          <MdxIcon name="ase" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
      <Column colLg={4} colMd={4} noGutterSm mdxType="Column">
  <ResourceCard subTitle="Elements package: Color" href="https://github.com/carbon-design-system/carbon/tree/master/packages/colors" mdxType="ResourceCard">
          <MdxIcon name="github" mdxType="MdxIcon" />
  </ResourceCard>
      </Column>
    </Row>
    <br />
    <br />
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`Application of the color palette brings a unified and recognizable consistency
to IBM’s vast array of digital products and interfaces. This consistency is
grounded by a set of well-defined rules on how to work with the Carbon component
library in the context of dark and light themes.`}</p>
    <img {...{
      "src": "/6e5da95292e87ff2a49a40d9289fcb09/IBM_UI_Screens.gif",
      "alt": "Color in UI"
    }}></img>
    <Caption mdxType="Caption"></Caption>
    <h2 {...{
      "id": "color-anatomy"
    }}>{`Color anatomy`}</h2>
    <p>{`Carbon’s default themes are derived from the IBM Design Language color palette.
The Neutral Gray family is dominant in the default themes, making use of subtle
shifts in value to help organize content into distinct zones.`}</p>
    <p>{`The core Blue family serves as the primary action color across all IBM products
and experiences. Additional colors are used sparingly and purposefully.`}</p>
    <ColorGrid colorFamily="blue" mdxType="ColorGrid" />
    <ColorGrid colorFamily="gray" mdxType="ColorGrid" />
    {
      /* possible TODO: remove inline style */
    }
    <ColorGrid colorFamily="alerts" style={{
      "marginTop": "2rem"
    }} mdxType="ColorGrid" />
    <Caption mdxType="Caption">Alerts Colors</Caption>
    <h2 {...{
      "id": "implementing-color"
    }}>{`Implementing color`}</h2>
    <p>{`Carbon uses `}<em parentName="p">{`tokens`}</em>{` and `}<em parentName="p">{`themes`}</em>{` to manage color. Tokens are role-based, and
themes specify the color values that serve those roles in the UI.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Term`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Definition`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Theme`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The set of unique values assigned to the tokens of a Carbon interface`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Token`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The code identifier for a unique role or set of roles. Tokens are universal and never change across themes.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Role`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The systematic usage(s) of a token. Roles cannot be changed between themes.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><em parentName="td">{`Value`}</em></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The actual style (i.e. hex code) assigned to a token`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "tokens"
    }}>{`Tokens`}</h3>
    <p>{`Color in Carbon is managed using color tokens. Tokens are a method of
abstracting color by role or usage, independent of the actual color values. For
example, instead of needing to code all instances of input labels in a UI to be
`}<inlineCode parentName="p">{`#565656`}</inlineCode>{`, the `}<em parentName="p">{`form`}</em>{` component in Carbon specifies the token `}<inlineCode parentName="p">{`$text-02`}</inlineCode>{` for
these lables, and that token is specified in a separate theme file to be
`}<inlineCode parentName="p">{`#565656`}</inlineCode>{`. For a different theme, that same `}<inlineCode parentName="p">{`text-02`}</inlineCode>{` token could be mapped to a
different hex value, like `}<inlineCode parentName="p">{`#ffffff`}</inlineCode>{`. Thus, tokens not only allow for more
efficient color updates within a theme, but also enable any UI (or portion of a
UI) built with Carbon to easily switch between different themes.`}</p>
    <p>{`A single token can be associated with multiple roles, but only if the color
value is used consistently across those roles. This allows for uniform color
application across themes while maintaining full styling flexibility.`}</p>
    <p>{`With this system, all Carbon users can create their own themes by assigning new
values to the established color tokens. IBM products should use one of the four
IBM default themes.`}</p>
    <h3 {...{
      "id": "themes"
    }}>{`Themes`}</h3>
    <p>{`Themes serve as an organizational framework for color in Carbon, with each theme
based on a specific primary background color. There are two default “light”
themes and two default “dark” themes. The light themes use White and Gray 10
backgrounds, and the dark themes use Gray 100 and Gray 90 backgrounds. Default
color tokens are provided for each component based on the primary background
color.`}</p>
    <Row className="color-blocks" mdxType="Row">
      <Column colSm={1} colMd={1} colLg={2} mdxType="Column">
        <div className="bx--aspect-ratio bx--aspect-ratio--1x1">
  <div className="bx--aspect-ratio--object color-square color-square--white">
    White
  </div>
        </div>
        <div className="bx--caption">Light</div>
      </Column>
      <Column colSm={1} colMd={1} colLg={2} mdxType="Column">
        <div className="bx--aspect-ratio bx--aspect-ratio--1x1">
  <div className="bx--aspect-ratio--object color-square color-square--gray-10">
    Gray 10
  </div>
        </div>
      </Column>
      <Column colSm={1} colMd={1} colLg={2} mdxType="Column">
        <div className="bx--aspect-ratio bx--aspect-ratio--1x1">
  <div className="bx--aspect-ratio--object color-square color-square--gray-100">
    Gray 100
  </div>
        </div>
        <div className="bx--caption">Dark</div>
      </Column>
      <Column colSm={1} colMd={1} colLg={2} mdxType="Column">
        <div className="bx--aspect-ratio bx--aspect-ratio--1x1">
  <div className="bx--aspect-ratio--object color-square color-square--gray-90">
    Gray 90
  </div>
        </div>
      </Column>
    </Row>
    <br />
    <h4 {...{
      "id": "global-background-colors"
    }}>{`Global background colors`}</h4>
    <br />
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Primary Background`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Token`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Hex value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`White`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global Background Light`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ui-background`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><ColorBlock showhex size="xs" mdxType="ColorBlock">{`#ffffff`}</ColorBlock></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Gray 10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global Background Light`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ui-background`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><ColorBlock showhex size="xs" mdxType="ColorBlock">{`#f3f3f3`}</ColorBlock></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Gray 90`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global Background Dark`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ui-background`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><ColorBlock showhex size="xs" mdxType="ColorBlock">{`#282828`}</ColorBlock></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Gray 100`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Global Background Dark`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`$ui-background`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><ColorBlock showhex size="xs" mdxType="ColorBlock">{`#171717`}</ColorBlock></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <br />
    <h4 {...{
      "id": "light-themes"
    }}>{`Light themes`}</h4>
    <p>{`Gray 10 components are paired with White backgrounds to make the White theme and
White components with Gray 10 backgrounds to make the Gray 10 theme. Some
components, such as buttons and toggles, share the same colors across both light
themes.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="Gray 10 dropdown on White background." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABFUlEQVQ4y72U6aqEMAyFff/XE/zngrjv+665nEIvdcZOh8GZwiG0Tb4ktVajm4f2FeBxHMTtJxJj/yvkG58MMVYTs+z7TvM807quJy3LItW2bc8VciBgruuSZVnMmqbJbNM0l8qyjMWcgGKF0zQxpyRJqCgKprIspcCqqmgcRzkQm2EYUhAEzLnrOmrb9kmAcTsMgxwIB7Rr2zZ5nkdRFLEEj4rjmAmJEfMSaBgG6bpOjuOwAN/3T8IaT4o5OnnZMjKjMpwdb0sU1tI0pTzP1S0DCGcImWUfg59lXddqINrAVUEFVxU+Sgnk1wXZVTAlEJu4h6gOQuuYA34lHEvf93IgfiU4APyuEPO7x+G25+su4Fde7D8v8xsZLRdbAgAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Gray 10 dropdown on White background.",
              "title": "Gray 10 dropdown on White background.",
              "src": "/static/e39f2fd3b3b8698524bb28e259111fca/9eefd/Light_theme_01.png",
              "srcSet": ["/static/e39f2fd3b3b8698524bb28e259111fca/d6747/Light_theme_01.png 288w", "/static/e39f2fd3b3b8698524bb28e259111fca/09548/Light_theme_01.png 576w", "/static/e39f2fd3b3b8698524bb28e259111fca/9eefd/Light_theme_01.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="White dropdown on a Gray 10 background." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABKUlEQVQ4y81Ux46FMAzk/78PidsDLvTeu1djKchAltf2sJGGJCYzuOAYeZ5TWZZUFAXP38LAo67rP4MBz7CoqorxroDiKeweSqMM4UzQQfIPgk3TUN/3NI4jDcOwAzYd8A4cGdlBEIdQpDAMKcsyCoKAZ4xt23ao/TRNLPqrYNd1fKBtW/YShHmembyu6wWwgyPDvoSMNQoFITWkd2dPwZEah98mSRJyHIfheR5FUcThnwG7egeOtihAHMdkWRaZpkmu65Lv+ywsAZv9eJBt2/tHb6uM/GGN/J1DRN4wkDcU8DZkbCCGgyDIYuhEVQ7BuRXEPk1TrvJdQV4WRKgQW5ZF+6tIPBVEPlSXQFh1w8ce3vWq7lLArPr9LcFXL4dL6316B/5/DyV+AJYzBVGK7r7sAAAAAElFTkSuQmCC')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "White dropdown on a Gray 10 background.",
              "title": "White dropdown on a Gray 10 background.",
              "src": "/static/b0f40c28c5434c5a2816ef779316fdeb/9eefd/Light_theme_02.png",
              "srcSet": ["/static/b0f40c28c5434c5a2816ef779316fdeb/d6747/Light_theme_02.png 288w", "/static/b0f40c28c5434c5a2816ef779316fdeb/09548/Light_theme_02.png 576w", "/static/b0f40c28c5434c5a2816ef779316fdeb/9eefd/Light_theme_02.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="White overflow menu on Gray 10 card." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADdklEQVQ4y72U60+bVRzH+Q/2whkT9cWyOJ3BGZgjMYhjrKPSboAMiqYqOjYn6DbJQsJlEDExzmySxehgF0UuC+JwRrYwX8zINnR422ZtSynUcikim22hBVoolI/nnKWjs+gLTTzJN9/zPMnzeX6Xc35xi4uLyCV9YWGBcDisPFrz8/P/qGhGXGTzX1eEERfZ+P0+ui91YbOauNLdRc83F/m2+yJfdp7lVHMjLc0f09TUQGPjRxw9+gHHj9dzQuhYfR1ej2cpQpmiXCMjQ5SV7kWfsZkdz+dRtNOoPF2TQkryBlavuo+Vd63g/nvv5p6VK1gXv4aERx7koTWrsNl6FUOybgPtfTb27S0mO1PH7kIjxvws8nO3kpSUyFZ9OpotqWx4LIHXigrJzdZjfHY7zxhy2F/yKn3i2xhgf7+dDK0G3VMa2k+3YjZdo6qqkoIcPe+8bKCw8AU2bU7lbMdnVFbs562aSt6sPsB3V69isZiXA/aRviUNjUbDp20t/NDTTdOHdby040UOlRbTWVtKXl4WmzYmsz5xHToRcW62jtrDB3END8YCZQ2LdxfwdFYGOVl6Eh+NJ37tAyQ8vJr8bVpOH6nhvYNvcKCihILn8nh9zy502lSqy0twOh1LwEiX3X/c4ExbI+e/aOVk3RH2FO8SNdrO4ZpyykqK2CfqutOQiU6vVT815G4TYAPlZRL4ayzw5s1xWhrqOfd5K22nGvik+SQd7S1c/uocF86fobm+lqpXjORkanni8fU8mZKklJaWrMoVA5ycnBDnroufr32Po9/KL9d/FGexS+n6Tz1cufw1ly500tnRzolj7/PuobeprqpQjRsbG7sTGIGOj9/AYrWKrlnweLwEg7PMzAQIBAJqHwgGlYfUlVtYuiXhqKsXAUq5XC5MJhNms1mdLZvNRm9vr3KHYwCfz8fU1JTIZlLt5T2em5tTvixwdHRUwCxYRZQSMjAwwODgIENDQwLowOv1Mj09zcTEhHK5lgVG8ve43fw26sI1MqyilVHIdGdmZpQkUIKdTid2u125W3wjJ1IsULyc9E/j9QWE34LI9Px+v5KEy6gkVKY8Ozur9h4xGO4YX7cfwguMu/30u/wM/34LICdQNFD+RAJkypFZGBSN+tuUQyFRDyHpsjZ/lYwqFAopBVXHg+rd/zNgo7v9bxRh/Ak181FTtc+2zwAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "White overflow menu on Gray 10 card.",
              "title": "White overflow menu on Gray 10 card.",
              "src": "/static/b7e7848d2bcce0c0eca3f6537c5962d6/9eefd/Light_theme_03.png",
              "srcSet": ["/static/b7e7848d2bcce0c0eca3f6537c5962d6/d6747/Light_theme_03.png 288w", "/static/b7e7848d2bcce0c0eca3f6537c5962d6/09548/Light_theme_03.png 576w", "/static/b7e7848d2bcce0c0eca3f6537c5962d6/9eefd/Light_theme_03.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample caption="Avoid use of midtones." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABQElEQVQ4y7VUWa6DMAzk/rdB4o9VFMQmyqqyw1H8OpFSJRCoVPE+Rg4mHsYL1hzHoTuh/Ruhbds/k4ixmuiAtSyLTNOUAN8ZxFhAEx2+71OSJJSmqQT4VMA7xBwUAvgaLlRVRVmW0fP5ZM9FUdCyLAfM80zjOLK7iP0QcnY44zimruuoaRpmgb7vWaAKIIVSlEVJGEURIyvLkoZh+CiZpkmJdV2ZCE54qKHYFM/z6PF4KBEEwefM4yRCUaVhGKTrOjuD1HVdCfDxzuO87/Qh5dfrRXVds5RV6cKH2g7v2l6mLDalbVtGeFY7AE1BjS8JoRDq8jxnxFcN+doUTghlLKW3PRsXjq8K8ZITYv5QT5z5+Oyxbdv12KBrGIk9wjA8gPsRI3V5vxxUy2C/LPgdWDFW+lPuWF9Khb9AUnj3xv4Dn6oq21BqaBkAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Avoid use of midtones.",
              "title": "Avoid use of midtones.",
              "src": "/static/a00ab6863dee1c27cbad7b4b2608c1da/9eefd/Light_theme_04.png",
              "srcSet": ["/static/a00ab6863dee1c27cbad7b4b2608c1da/d6747/Light_theme_04.png 288w", "/static/a00ab6863dee1c27cbad7b4b2608c1da/09548/Light_theme_04.png 576w", "/static/a00ab6863dee1c27cbad7b4b2608c1da/9eefd/Light_theme_04.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <img {...{
      "src": "/db2bb52890da29f3391f480d846b5a38/Light_theme_sample_revised.gif",
      "alt": "light theme examples"
    }}></img>
    <Caption mdxType="Caption">Components with common colors across both light themes.</Caption>
    <br />
    <h4 {...{
      "id": "dark-themes"
    }}>{`Dark themes`}</h4>
    <p>{`Gray 90 components are paired with Gray 100 backgrounds to make the Gray 100
Theme and Gray 80 components with Gray 90 backgrounds to make the Gray 90 Theme.
Some components, such as buttons and toggles, share the same colors across both
light themes.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="Gray 90 dropdown on Gray 100 background." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABS0lEQVQ4y7WUaY6CQBCFOYSAJIBhEUQEZFMDmDHG+1+pJq+SNg2yZAzz40vTRderpRdF13VaE+XfBDVN+1pE9lUwEQaMqqqSsAlgGyKvFb5AkQ2GYZBt22SaZg/LsiaBTy9D8YFoELvdbtR1HY/3+53Hw+FAURS9wRxkWUa73Y59eyULQURM05TO5zOdTieK45gRAkOOxyMnMStYFAVVVcWLgyCgMAw/gJgY4TMp6DgONU3D1HVNeZ5zgCGoAOUisOu604L4+Xw+6fV6ce/KsmQnGQiit4+fB889z6PNZjNdMqLDCb0TZcns93u6XC7Uti23ZLGH2BSUgx6ObQR2GUK+7/Oa2R7iJ8q4Xq+8y2MZyjaIL2YIISCftymwZjHDJEneoigf8+HBFsFmzyHG7Xb7ce2WGL16w8fhL8w+Dqs8X8MMv0HWWP3F/gUJKBscmHl40AAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Gray 10 dropdown on White background.",
              "title": "Gray 10 dropdown on White background.",
              "src": "/static/e63eb052d9ce2b5db376e61cf3488c6a/9eefd/Dark_theme_01_new.png",
              "srcSet": ["/static/e63eb052d9ce2b5db376e61cf3488c6a/d6747/Dark_theme_01_new.png 288w", "/static/e63eb052d9ce2b5db376e61cf3488c6a/09548/Dark_theme_01_new.png 576w", "/static/e63eb052d9ce2b5db376e61cf3488c6a/9eefd/Dark_theme_01_new.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="Gray 80 dropdown on Gray 90 background." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABQklEQVQ4y7WU3YqDMBCFfRlrqVAVlIK2aq1af1GkF/r+DzLLGYhk17S6pb04jMaZL5mTGG2/39MnpX0NaBjG2xC5VpMHEHe73Ry3SK6FNHnANE2ybZuOx+Msy7KeCrmoWawQwmyO41DTNNR1Hce+76mua7pcLhSG4UJpmnINamegoGMQMyZJwonX65XiOGYBqFIURVzzFIg28jyn+/3OyUEQ0Pl8XggwRKzyKVDXdXJdl9sUrRZFQWVZchTCOybExMjxPI9rlUB8HMeRpmliH6uq4iJZAA7DQI/Hg30+nU5qoPAQ/mVZxt6J9mTBhrIoqW1bfn65KfDwdrux4KFqI+AbQL7vb9sU+IRWscuqFf6FrwLFcUHyK9imYyPOIQQwWsez6lCvAhEPh8Ov326LULN6OfxHLy+Hr1xf70hmfPzG/gHnRnFKdjVZ2gAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "White dropdown on a Gray 10 background.",
              "title": "White dropdown on a Gray 10 background.",
              "src": "/static/40d89a91d11e0054e3aeeed564002359/9eefd/Dark_theme_02_new.png",
              "srcSet": ["/static/40d89a91d11e0054e3aeeed564002359/d6747/Dark_theme_02_new.png 288w", "/static/40d89a91d11e0054e3aeeed564002359/09548/Dark_theme_02_new.png 576w", "/static/40d89a91d11e0054e3aeeed564002359/9eefd/Dark_theme_02_new.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <Row mdxType="Row">
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" caption="Gray 70 is sometimes used when compound components are being applied, such as an overflow menu." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAADj0lEQVQ4y51UW28bRRjNL2nr+9rrXXvTJE7i2/pSe31fr29JaOPWre3ElpKoTQmpQ9ObSLkJISTUh75AgZKCLBVaJCQQKn2wVAkkxA86/WYsR3aa8sDD2W/m29kz53zz7UwJggCHwwEWnU4nXATnOCgvHMNw/RCjb0dxajSw2+0wmcw4dfo0j2dMJoomWCxW/s5uH37MwDYSBOeRiHHSqRGZxyOjkM/gSqOOkp5F2cjB0HOIR8NQPBK8HjdkWYQkiTBbTLDZLBAcNljMZ4h0TCF7WK1WLCz4cO92D8u1IjY7DexcXcdm9zJKxTTSWhSK7ILZfAouwQobRf/CWajBOXgkAaLomiRkEzclO2tXkM0k0FitoWZkSKkGZVqGloxAjQSgeN1oN+vQswlUy3lUSgV02o03FTLLsiwheS5CamL44fA7/PvP39jb28UqKbzfrKDRuACV7P/09Efc2L2Ku/u7uLO/hz8HA7JvO07ogCy5EY+piMVj+P7xI7wavMRXD7/ExUt1fLS9juefbqNMqtTQIqYVCVoshLKewcEHtzEzrXDSI0I2mZubwVrzAop5DXo+RTUTIVK9vE4zqmTxyWe38PnBPnq7W1h9p4wNqm86qVKtu3RgEnfJCZl/i8WCcCiIw28f4ln/Gzz44hOstxq8Rh/evI7tjRa69RouGWkkkjHks+eoA1I4v1LC1sYadcgEoZMTqmoI/cOv8eK3Z3j+9Al+7j/G77/08dfgDwxe/Ir+owc4uNbE+aUiEvEgtEQY6VQUSYoSlYuVbdLy7Ayub2/i5t4OPr5/F/fuvI/ee9fQ26EDuNXDDYrvbnXQaV1EIafxWp6d9vC+HG/uo7ZhyVg8jqJhQC8WEQqFqK4++Obn4fPN8zhL89nZOT6eJ+i6DsMoweU6oQ9ZMpVKYXl5GbVaDaVSiU61jAqBxXw+z98btCFDoVDgaxjpWwk1LYVqtYpKpcJJ2OJcLseRzWZ5vtlsotVqUV82+MaMWBTFNwlZMhqJkAoN6XSKq2G2FxcX4ff7ybaPb9DtdtFut3nsdDpYWlo6WaHbLSIQDMEfVOEPDIkCgQCCwSAHmzPbjIypY6VpkdrLNB6RHfuXRfq1EohpRUQTGYTDYa5wBEbOVNfrdaysrHCrRTq8TCZzsmUnyVYUL10A3mFUlBMhyzIHG3tpLcNE24xfjsOL9L8xumSP5yYu2PEa/F+MOF4Du8LDc5idHmIAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "White overflow menu on Gray 10 card.",
              "title": "White overflow menu on Gray 10 card.",
              "src": "/static/35627308af7187849970a47a0b72f238/9eefd/Dark_theme_03_new.png",
              "srcSet": ["/static/35627308af7187849970a47a0b72f238/d6747/Dark_theme_03_new.png 288w", "/static/35627308af7187849970a47a0b72f238/09548/Dark_theme_03_new.png 576w", "/static/35627308af7187849970a47a0b72f238/9eefd/Dark_theme_03_new.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample caption="Do not apply components that are darker than the background unless using high-contrast mode." mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABNklEQVQ4y72U2W6DMBBF8wsRhC1hJwkEENsDPKSiEv//TdPekRwZ4ZIupA9XY3k8x3ds8C5NU9pSu5cAr9crifgbybUPhyKRZdmPJNeyQ3mX8/lMYRhSEAQzYU4l5FCzcIgBdsMiLMjznOfKsqTL5UKHw4EMw5jJNE1yHIehqH0AxUAAT6cTL0I8Ho8sy7KUAnQViGQURewSuwtnKFQJeSVQtH273ajvexqGgdq2paqqFqrrmo+iKAoeo0ZmzIA4u3EcaZomBgPadd1MTdPQ2/1O75/rMAZ4AZRb9jyP20bLqnY1TeOLAmi/3z8/Q9d1OQIIgOoycHa6rnPe9/11YBzHbB+3vHYhAvzUIZwBhogi27b/9tkAIIBoXzhVCW1/2bL8631XMJAkyT8+Dps9X1sBX/JifwATMDkWpehx3AAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "Do not apply components that are darker than the background unless using high-contrast mode.",
              "title": "Do not apply components that are darker than the background unless using high-contrast mode.",
              "src": "/static/2e77593918925c7ec25e07cf2a9b7cb5/9eefd/Dark_theme_04_new.png",
              "srcSet": ["/static/2e77593918925c7ec25e07cf2a9b7cb5/d6747/Dark_theme_04_new.png 288w", "/static/2e77593918925c7ec25e07cf2a9b7cb5/09548/Dark_theme_04_new.png 576w", "/static/2e77593918925c7ec25e07cf2a9b7cb5/9eefd/Dark_theme_04_new.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <img {...{
      "src": "/7a722443efb1bdbfb103e00ac307a915/Dark_theme_sample_revised.gif",
      "alt": "dark theme examples"
    }}></img>
    <Caption mdxType="Caption">
  Components with common colors across both dark UI backgrounds.
    </Caption>
    <br />
    <h4 {...{
      "id": "high-contrast"
    }}>{`High contrast`}</h4>
    <p>{`In some cases, it is helpful to apply light components to dark backgrounds or
dark components to light backgrounds. This technique is useful to focus
attention or create visual tension.`}</p>
    <Row mdxType="Row">
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAABtUlEQVQ4y+WUPW/aUBSGWbJm6M8IitRCSWgrGpyG4GBUPmzH7sJXQSxMyciQWG1QgC5VpbgT/6i/AxgY2qpIfPkJvqVRRRxQpKhSlSO9eq/t+z46tu6xjwcu3/8FdBznxr30956VwOXAfbSc9+zQC3xXLT+71eF0OmU4HNLv9+n1ekLdbpfBYMB4PGY0Gt34ZDLx7tC9mM1m4kan0yGXy1Gr1SiXy1QqFYrFIoVCgWq1KtyVYRjYti0ybvYW0O3MrUajgaIo5PN50uk0qVRKeCaTEetYLEYkEsHv91Ov19cD2+028Xgc0zTRdV3I7UY/NlA1HU03SChJAsHnWJa1HthqtZBlmWw2i6qqAuiusykFQ01iam9JxCWCT7c4Pz9bD2w2mwQDz9iXJKLRPSThUcIHJoFYidBhibD8nq0XKmfWxXrgx4sG28FXvN5P8HJPZmd3l1AoxKbxjScnsFFx8OXnwcyAU+vrAsjdwKsrm6NEEsN8J76bpmlCh7kPHJRt3pS+IJVswseXfPr8B+hxsFdPipv6BZPv84P6Y6Gf883zV/0dWj3LnhPhoUUI55/N8uP4wV4D9g2VdPFegiMAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "High contrast example in a light theme.",
              "title": "High contrast example in a light theme.",
              "src": "/static/b59bd9a10c9c2e75cfafcaa02dcd6474/9eefd/High_contrast_01.png",
              "srcSet": ["/static/b59bd9a10c9c2e75cfafcaa02dcd6474/d6747/High_contrast_01.png 288w", "/static/b59bd9a10c9c2e75cfafcaa02dcd6474/09548/High_contrast_01.png 576w", "/static/b59bd9a10c9c2e75cfafcaa02dcd6474/9eefd/High_contrast_01.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
      <Column colMd={4} colLg={6} mdxType="Column">
  <DoDontExample type="do" mdxType="DoDontExample">
          <span {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1088px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "100%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsSAAALEgHS3X78AAAB3klEQVQ4y+WUy47ScBSHeQE3ruWmI6VcDMZENrDzMXyFWfkObpSFCRMXxgRCgpPALHChW4wJhHAzkVIplzDcxDihyMYADp9tDYrAwCxmY2zy5fxzTs+vp6fn1OR0OrlKTP+eoCAIF968K7ZV8DIJ+x5iWjqXAd06HA5W/UvfOqs5y/NGhS6XC5/PRyAQ+E0wGMTv9+P1eg08Ho9h3W739gp1RFHEarUSCoUol8uk02lyuRzZbJZ8Pk+hUCCTyVAsFimVSsiyTDgcxm63G7kbgnplZrOZeDxOu902ROr1Os1m07DL83A4ZDKZoF+pVAqbzbZbMBKJMBgMqFQqRhU6kiRpVKnKNaSqzGmny/zHOYlEYr9gNBql1+ujKHVqtZohqCgK3XaDTkum3aiifu1r9S04OUnuF4zFYizO56ijM8bqiNFohKqqfGiMeffxG+8rqoHUXxA/Tmo93Cl4Q+vhMeMpnH75TvdsznQ6Yzab8iAE1w5/YXoI1x/B0xevEQ6sCM4LBC0Wi/bljuj0tB5KMtVPivG6OpG3TZ4kWoSSLR6/avD8zWeeHb3k1k27li9uDvaqFZejtDJf9+4I+O8K3Pf9sS5RH2g9vjbY65uyjQOHxu2/udSmXMku/18/2J+7bOvlIfRbXQAAAABJRU5ErkJggg==')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "High contrast example in a dark theme.",
              "title": "High contrast example in a dark theme.",
              "src": "/static/39c592a269815d66822da661d04899c2/9eefd/High_contrast_02.png",
              "srcSet": ["/static/39c592a269815d66822da661d04899c2/d6747/High_contrast_02.png 288w", "/static/39c592a269815d66822da661d04899c2/09548/High_contrast_02.png 576w", "/static/39c592a269815d66822da661d04899c2/9eefd/High_contrast_02.png 1088w"],
              "sizes": "(max-width: 1088px) 100vw, 1088px",
              "loading": "lazy"
            }}></img>{`
    `}</span>
  </DoDontExample>
      </Column>
    </Row>
    <h3 {...{
      "id": "interaction-states"
    }}>{`Interaction states`}</h3>
    <p>{`In addition to the enabled-state tokens above, the five other primary
interaction states have been defined and tokenized for each default theme. For
values between Black and 70, interaction gets lighter, and for values between 60
and White, interaction gets darker.`}</p>
    <h4 {...{
      "id": "hover"
    }}>{`Hover`}</h4>
    <p>{`Hover states have their own tokens. In the IBM default themes, these colors fall
outside of the core palette steps and act as “half steps” between two adjacent
colors. The White theme shares the same hover value as the Gray 10 theme; the
two dark theme backgrounds share a hover value as well.`}</p>
    <h4 {...{
      "id": "active"
    }}>{`Active`}</h4>
    <p>{`The active state is two full steps lighter for values between 100 and 70 and two
full steps darker for values between 60 and 10. For example, the Blue 60 active
state is Blue 80. The exception is that White shares the same active state as
Gray 10, and Black shares the same active state as Gray 100.`}</p>
    <h4 {...{
      "id": "selected"
    }}>{`Selected`}</h4>
    <p>{`The selected state is one full step lighter for values between 100 and 70, and
one full step darker for values between 60 and 10. For example, the Blue 60
selected state is Blue 70. The exception is that White shares the same selected
state as Gray 10, and Black shares the same selected state as Gray 100.`}</p>
    <h4 {...{
      "id": "focus"
    }}>{`Focus`}</h4>
    <p>{`The focus state usually appears as a Blue 60 border in the light theme and as a
White border in the dark theme.`}</p>
    <h4 {...{
      "id": "disabled"
    }}>{`Disabled`}</h4>
    <p>{`Disabled components are unavailable for interaction, so they don’t receive hover
or focus and are not subject to WC3 contrast compliance standards. For the dark
themes, disabled values are two full steps lighter than their respective
background. For light themes, they are two full steps darker. The exception is
that the White theme disabled value is Gray 30.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "51.78571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABOUlEQVQoz6VS247CIBTs//+arz7oRhMv2WpdEQqlIAVmOadWjdldH/YkpB2GGc6FCiVyzrye/9/hKV5x9Uo+x1/C33BFIMaIEAL6vufNlBJj7/1dMAwDr4l3zrEup4g4hILtw/B8PqNpGlhrWbTZbLDf7xm3bYvZbIb5fI6u6yClxGKxwPF4LIYJ21pgvT2gudhy0c1QKcWmJCBDMq/rGsYY3lsuP7BarfgCrTV2ux1OpxNnePhqsf0sCYkOacrQGA0hBJdM5bRaQSrJZcc4MCZj4vzV4SIFYyrRWlOyFnC9pU6OQ1Gi5/THXkXoi0dpDYezAUZd7700ykELfx+Elh4xPAZT0aC8zaWx48SoD85kBD/iIWTmp4le+8yLsmFtd+PTyFdE/Pxs3j+TZ+10QfWfh0yfx/nR9Bs6ng9Y0f47wQAAAABJRU5ErkJggg==')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "interactive states light",
        "title": "interactive states light",
        "src": "/static/c31ab0ff67b7b2760ca015d3ec2021ff/fb070/Interactive_states_light_new.png",
        "srcSet": ["/static/c31ab0ff67b7b2760ca015d3ec2021ff/d6747/Interactive_states_light_new.png 288w", "/static/c31ab0ff67b7b2760ca015d3ec2021ff/09548/Interactive_states_light_new.png 576w", "/static/c31ab0ff67b7b2760ca015d3ec2021ff/fb070/Interactive_states_light_new.png 1152w", "/static/c31ab0ff67b7b2760ca015d3ec2021ff/fb104/Interactive_states_light_new.png 1728w", "/static/c31ab0ff67b7b2760ca015d3ec2021ff/8fefe/Interactive_states_light_new.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Light theme interactive tokens.</Caption>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "51.78571428571428%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAYAAAC0VX7mAAAACXBIWXMAAAsSAAALEgHS3X78AAABaklEQVQoz41S246CUAzkH+RwR24LGIigYU00UeOLxvjA6hqjvvn/PzF7WgJi1mT3oaTTmc4paRVN06DrOkc//wtT/g4rBIQQHC1BeSt+19hq32GFCo7jIAxD5HnORcJxHHMQb1kWkiSB53kwDAOu67KW6hS+7yPLssaQ3BeLBXa7HabTKQsOhwPquuamNE3xeDxwv9/5AdLcbjdst1uYponNZoPj8YjlcvmccD6fY7/fo6oqNiTzuv5CWZZscr1ecblcOKfa+fvcGa7Xa5xOJ6xWq6dhlmeYzWY8Df1SURZsTr9i2zYmkxJFUfBjYRig+qwwHo/ZIB2lrKXebilxNJKkgYE6gGVa+Agk1gyoqgrP9RF6MedCqIiCRPIpY+qNZG7oJnM8IX08N5JGNr9AEwbSwLU9iQVsy2G+2ajA0PE5NL3ZvC+5oRt0G1eatasvZ9Jg0Zn0z0IQ1sSLtsXdhP86bO33Yfe1be0HKzQTNYYAi3QAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "interactive states dark",
        "title": "interactive states dark",
        "src": "/static/07e85689bc8936e0d1e8864bec0115d7/fb070/Interactive_states_dark_new.png",
        "srcSet": ["/static/07e85689bc8936e0d1e8864bec0115d7/d6747/Interactive_states_dark_new.png 288w", "/static/07e85689bc8936e0d1e8864bec0115d7/09548/Interactive_states_dark_new.png 576w", "/static/07e85689bc8936e0d1e8864bec0115d7/fb070/Interactive_states_dark_new.png 1152w", "/static/07e85689bc8936e0d1e8864bec0115d7/fb104/Interactive_states_dark_new.png 1728w", "/static/07e85689bc8936e0d1e8864bec0115d7/8fefe/Interactive_states_dark_new.png 2240w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Dark theme interactive tokens.</Caption>
    <h2 {...{
      "id": "accessibility"
    }}>{`Accessibility`}</h2>
    <p>{`Using various forms of contrast is the most important consideration when making
user-friendly color and interface choices. Awareness of standards and best
practices is the key to accessible color selection.`}</p>
    <p>{`The color families in the IBM palette contain ten values from 10 to 100. White
and Black sit outside those values. Black text is WCAG AA accessible on colors
ranging from 10 to 50. White text is accessible on colors from 60 to 100.`}</p>
    <ArtDirection mdxType="ArtDirection">
      <img {...{
        "src": "/242a3d38ca259abe058c71994dfab7e3/Duo_Mobile_Color_in_UI-Digital_Accessibility_WCAG_2_Standards_01.svg",
        "alt": "Digital accessibility"
      }}></img>
      <img {...{
        "src": "/9c0345a6184cee6d00da15e3f9133a20/wcag2-blues.svg",
        "alt": "Digital accessibility"
      }}></img>
    </ArtDirection>
    <p>{`Beyond black and white, the IBM color palette provides a range of accessible
combinations. Subtracting the foreground value from the background value (or
vice versa) helps determine whether that color combination meets the WCAG AA
contrast ratio success criteria. If the difference between two values is 50 or
greater, the colors are accessible. Anything below a difference of 50 may fail
accessibility standards.`}</p>
    <ArtDirection mdxType="ArtDirection">
      <img {...{
        "src": "/e086a893e18f6baa0de52d02fc7d38b2/Duo_Mobile_Color_in_UI-Digital_Accessibility_WCAG_2_Standards_02.svg",
        "alt": "Blue color do dont accesible example"
      }}></img>
      <img {...{
        "src": "/05806dc5ff730df26c2842af66050b30/wcag-dos-donts.svg",
        "alt": "Blue color do dont accesible example"
      }}></img>
    </ArtDirection>
    <Caption mdxType="Caption">Blue color family, 60 background</Caption>
    <p>{`For a complete look at color-related accessibility topics in Carbon, visit the
`}<a parentName="p" {...{
        "href": "/guidelines/accessibility/color"
      }}>{`Accessibility`}</a>{` section.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      